import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { ArrowRight, ChevronDown, ChevronRight, RotateCw } from 'lucide-react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { ErrorState } from '../../components/PageState';
import BulkReviewModal from './BulkReviewModal';
import { SeverityIcon } from './SeoFindings';
import { shortSha, timeAgo } from './ui';

const SECTIONS = [
  { severity: 'ERROR', title: 'Broken', empty: 'No broken links or missing files.' },
  { severity: 'WARN', title: 'Should be fixed' },
  { severity: 'INFO', title: 'Work, but take a detour' },
];

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

const GroupRow = ({ group, selectable, selected, onToggleSelected, onOpenPage, onOpenShared }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="mse-link-group">
      <div className="mse-link-group-head">
        {selectable ? (
          <Form.Check
            type="checkbox"
            checked={selected}
            onChange={onToggleSelected}
            aria-label={`Fix ${group.href}`}
            className="mse-link-select"
          />
        ) : (
          <SeverityIcon severity={group.severity} />
        )}
        <div className="mse-link-group-main">
          <div className="mse-link-hrefs">
            <code>{group.href}</code>
            {group.fix && (
              <>
                <ArrowRight size={12} className="mse-sev-info" />
                <code>{group.fix}</code>
              </>
            )}
          </div>
          <div className="mse-finding-message">
            {group.title}. {group.message}
          </div>
        </div>
        <button type="button" className="mse-link-button mse-link-count" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {group.inChrome ? 'in the shared header or footer' : `on ${plural(group.pages, 'page')}`}
        </button>
      </div>
      {open && (
        <ul className="mse-link-sources">
          {group.inChrome && (
            <li>
              <button type="button" className="mse-link-button" onClick={() => onOpenShared()}>
                Edit the shared header and footer
              </button>
              <span className="mse-hint"> (on {plural(group.pages, 'page')})</span>
            </li>
          )}
          {!group.inChrome &&
            group.sources.map((s) => (
              <li key={s.path}>
                <button type="button" className="mse-link-button mse-mono" onClick={() => onOpenPage(s.path)}>
                  {s.url}
                </button>
                {s.count > 1 && <span className="mse-hint"> ({s.count} times)</span>}
              </li>
            ))}
        </ul>
      )}
    </li>
  );
};

/**
 * Site-wide link check: broken links and missing files first, then links that work but go through
 * a redirect (fixable in bulk), then external hosts so typos stand out.
 */
const LinksReport = ({ canEdit, siteUrl, onOpenPage, onOpenShared, onSaved }) => {
  const ensureToken = useAuthToken();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [showFix, setShowFix] = useState(false);
  const [showHosts, setShowHosts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await ensureToken();
      setReport((await marketingSiteService.siteLinks()).data);
      setSelected(new Set());
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not check the links.'));
    } finally {
      setLoading(false);
    }
  }, [ensureToken]);

  useEffect(() => {
    load();
  }, [load]);

  const bySeverity = useMemo(() => {
    const out = { ERROR: [], WARN: [], INFO: [] };
    for (const g of report?.groups || []) out[g.severity].push(g);
    return out;
  }, [report]);

  const fixable = useMemo(() => (report?.groups || []).filter((g) => g.fix), [report]);
  const hrefs = useMemo(() => [...selected], [selected]);
  const loadPreview = useCallback(async () => (await marketingSiteService.linkFixPreview({ hrefs })).data, [hrefs]);
  const apply = useCallback(
    async ({ baseSha, message }) => (await marketingSiteService.linkFixApply({ params: { hrefs }, baseSha, message })).data,
    [hrefs]
  );

  const toggle = (href) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(href)) next.delete(href);
      else next.add(href);
      return next;
    });

  if (loading && !report) {
    return (
      <div className="mse-empty">
        <Spinner animation="border" size="sm" /> Checking every link on the site
      </div>
    );
  }
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!report) return null;

  const broken = report.counts.ERROR || 0;

  return (
    <div className="mse-health-report">
      <div className="mse-health-summary">
        <strong>{broken === 0 ? 'No broken links' : plural(broken, 'broken link')}</strong>
        <span className="mse-hint">
          {plural(report.linksChecked, 'internal link')} checked on {report.pages} pages at{' '}
          <code>{shortSha(report.headSha)}</code>, {timeAgo(report.computedAt)}. Redirects follow the {report.redirects}{' '}
          in the CloudFront function.
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          className="ms-auto"
          onClick={load}
          disabled={loading}
          title="Check again"
          aria-label="Check again"
        >
          {loading ? <Spinner animation="border" size="sm" /> : <RotateCw size={14} />}
        </Button>
      </div>

      {SECTIONS.map(({ severity, title, empty }) => {
        const groups = bySeverity[severity];
        if (groups.length === 0 && !empty) return null;
        const selectableHere = canEdit && groups.some((g) => g.fix);
        return (
          <section key={severity} className="mse-link-section">
            <h6 className="mse-section-title">
              <SeverityIcon severity={severity} /> {title}
              {groups.length > 0 && <span className="mse-group-count">{groups.length}</span>}
              {selectableHere && (
                <button
                  type="button"
                  className="mse-link-button ms-auto"
                  onClick={() => setSelected(new Set([...selected, ...groups.filter((g) => g.fix).map((g) => g.href)]))}
                >
                  Select all
                </button>
              )}
            </h6>
            {groups.length === 0 ? (
              <p className="mse-hint">{empty}</p>
            ) : (
              <ul className="mse-link-groups">
                {groups.map((g) => (
                  <GroupRow
                    key={`${g.rule}-${g.href}`}
                    group={g}
                    selectable={canEdit && !!g.fix}
                    selected={selected.has(g.href)}
                    onToggleSelected={() => toggle(g.href)}
                    onOpenPage={onOpenPage}
                    onOpenShared={onOpenShared}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}

      {canEdit && fixable.length > 0 && (
        <div className="mse-link-fixbar">
          <span className="mse-hint">
            {selected.size === 0 ? 'Tick links to point them at their final URL.' : `${plural(selected.size, 'link')} selected`}
          </span>
          <Button size="sm" variant="primary" disabled={selected.size === 0} onClick={() => setShowFix(true)}>
            Fix selected links
          </Button>
        </div>
      )}

      <section className="mse-link-section">
        <button type="button" className="mse-group-toggle" onClick={() => setShowHosts((v) => !v)} aria-expanded={showHosts}>
          {showHosts ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className="mse-group-name">External sites linked</span>
          <span className="mse-group-count">{report.externalHosts.length}</span>
        </button>
        {showHosts && (
          <>
            <p className="mse-hint">Not visited by the check. Scan the list for misspelt domains.</p>
            <table className="mse-health-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Links</th>
                  <th>Pages</th>
                </tr>
              </thead>
              <tbody>
                {report.externalHosts.map((h) => (
                  <tr key={h.host}>
                    <td className="mse-mono">{h.host}</td>
                    <td>{h.links}</td>
                    <td>{h.pages}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </section>

      {showFix && (
        <BulkReviewModal
          show={showFix}
          title="Fix links"
          description="Points each selected link at the URL it ends up at. Only the link addresses change."
          applyLabel="Save to test site"
          siteUrl={siteUrl}
          loadPreview={loadPreview}
          apply={apply}
          onClose={() => setShowFix(false)}
          onApplied={() => {
            load();
            onSaved?.();
          }}
        />
      )}
    </div>
  );
};

export default LinksReport;
